import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import {
  CheckCircle,
  Info,
  Warning,
  XCircle,
  CircleNotch,
} from '@phosphor-icons/react'
import { PostHogProvider } from 'posthog-js/react'
import { MotionConfig } from 'framer-motion'
import App from './App.tsx'
import { useThemeStore } from './stores/themeStore'
import { useAuthStore } from './stores/authStore'
import { useSenderEmailStore } from './stores/senderEmailStore'
import { useContactStore } from './stores/contactStore'
import { useCampaignStore } from './stores/campaignStore'
import { useInboxStore } from './stores/inboxStore'
import { useWorkflowStore } from './stores/workflowStore'
import { useBillingStore } from './stores/billingStore'
import { useNotificationStore } from './stores/notificationStore'
import { useTemplateStore } from './stores/templateStore'
import { useSignatureStore } from './stores/signatureStore'
import { resetOnAccountSwitch } from './lib/sessionReset'
import { initSentry, setSentryUser } from './lib/sentry'
import { initPostHog, identifyPostHog, posthog } from './lib/posthog'
import './index.css'

// Initialise Sentry before anything renders so its global error/rejection
// handlers are live for the whole app. No-op in dev / when no DSN is set.
initSentry()

// Initialise PostHog (product analytics / flags / surveys / web analytics).
// No-op in dev / when no key is set. Sentry owns errors + replay; PostHog adds
// only the product-analytics layer Sentry doesn't have.
initPostHog()

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Attribute Sentry errors AND PostHog product events to the signed-in user.
// One subscription covers every auth transition (login, register, SSO, token
// refresh, logout).
setSentryUser(useAuthStore.getState().user)
identifyPostHog(useAuthStore.getState().user)
useAuthStore.subscribe((state, prev) => {
  if (state.user !== prev.user) {
    setSentryUser(state.user)
    identifyPostHog(state.user)
  }

  // Wipe every per-user data store on an account *switch* (login, logout, or
  // A→B swap) and clear the React-Query cache, so the previous user's data
  // (sender_emails, contacts, campaigns, inbox, billing, …) can't be shown to
  // or 404 the ownership guard for the newly signed-in account. The id-vs-ref
  // guard (a token refresh is the same user with a new object) lives in
  // resetOnAccountSwitch — extracted to lib/sessionReset so it's unit-testable.
  resetOnAccountSwitch(state.user, prev.user, {
    stores: [
      useSenderEmailStore, useContactStore, useCampaignStore, useInboxStore,
      useWorkflowStore, useBillingStore, useNotificationStore, useTemplateStore,
      useSignatureStore,
    ],
    clearQueryCache: () => queryClient.clear(),
  })
})

function ThemedToaster() {
  const actualTheme = useThemeStore((s) => s.actualTheme)
  return (
    <Toaster
      position="top-right"
      theme={actualTheme}
      closeButton
      gap={10}
      offset={20}
      duration={4500}
      className="editorial-toaster"
      toastOptions={{
        classNames: {
          toast: 'ed-toast',
          title: 'ed-toast-title',
          description: 'ed-toast-description',
          actionButton: 'ed-toast-action',
          cancelButton: 'ed-toast-cancel',
          closeButton: 'ed-toast-close',
          icon: 'ed-toast-icon',
          success: 'ed-toast-success',
          error: 'ed-toast-error',
          warning: 'ed-toast-warning',
          info: 'ed-toast-info',
          loading: 'ed-toast-loading',
        },
      }}
      icons={{
        success: <CheckCircle weight="fill" className="h-[18px] w-[18px]" />,
        info: <Info weight="fill" className="h-[18px] w-[18px]" />,
        warning: <Warning weight="fill" className="h-[18px] w-[18px]" />,
        error: <XCircle weight="fill" className="h-[18px] w-[18px]" />,
        loading: <CircleNotch weight="bold" className="h-[18px] w-[18px] animate-spin" />,
      }}
    />
  )
}

// Cache the root on the container so Vite HMR re-running this module reuses the
// existing root instead of calling createRoot() twice on the same node (which
// triggers React's "container has already been passed to createRoot" warning).
const container = document.getElementById('root')! as HTMLElement & {
  _reactRoot?: ReactDOM.Root
}
const root = container._reactRoot ?? (container._reactRoot = ReactDOM.createRoot(container))

// framer-motion does NOT honor `prefers-reduced-motion: reduce` on its own —
// without a MotionConfig in the tree its default is `reducedMotion: "never"`, so
// every `whileHover`, `animate` and page-load stagger in the product plays at
// full amplitude even for users who asked the OS to cut animation. Mounting it
// at the root with `"user"` makes framer-motion read the media query and reduce
// transform/layout animations app-wide (opacity and colour still cross-fade, so
// state changes stay legible). It is a pure context provider with no DOM output,
// so it can sit outside the router without affecting layout or provider order.
// Keep it wrapping BOTH <App /> and <ThemedToaster />: sonner animates its own
// shell in CSS (already reduced-motion guarded), but anything we render *inside*
// a toast — custom toasts, DotLoader — is framer-motion and needs the context.
root.render(
  <React.StrictMode>
    <MotionConfig reducedMotion="user">
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
            <ThemedToaster />
          </BrowserRouter>
        </QueryClientProvider>
      </PostHogProvider>
    </MotionConfig>
  </React.StrictMode>,
)